import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { KitchenOrder } from "../types/index";
import { adminWalletOp } from "./callables";
// ... (Previous imports)

// ... (Keep existing getGlobalWallet, getWalletsByType, processOrderSettlement, runSettlementPayout)


export interface WalletData {
  id: string;
  ownerId: string;
  ownerType: "user" | "kitchen" | "chef" | "deliveryAgent" | "global";
  balance: number;      // Liquid cash (e.g. User Top-up)
  dueBalance?: number;  // Earnings owed to this entity (Kitchen earnings)
  lastUpdated: any;
  ownerName?: string;
  ownerPhone?: string;
}

// --- 1. CORE UTILS ---
// NOTE: wallets and wallet_transactions are server-write-only (firestore.rules).
// All mutations go through callables (functions/src/orderOps.ts adminWalletOp, payu.ts).

// Get Global Wallet (App Revenue)
export const getGlobalWallet = async (): Promise<WalletData> => {
  const ref = doc(db, "wallets", "global");
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() } as WalletData;
  }
  const res: any = await adminWalletOp({ op: "initGlobal" });
  return { id: "global", ownerId: "global", ownerType: "global", balance: 0, lastUpdated: null, ...(res?.wallet || {}) } as WalletData;
};

// Get Wallets by Type (with human-readable info)
export const getWalletsByType = async (
  type: "user" | "kitchen" | "chef" | "deliveryAgent"
): Promise<WalletData[]> => {
  const q = query(collection(db, "wallets"), where("ownerType", "==", type));
  const snapshot = await getDocs(q);

  const wallets: WalletData[] = [];

  for (const d of snapshot.docs) {
    const data = d.data() as any;
    const ownerId = data.ownerId;
    let ownerName: string | undefined;
    let ownerPhone: string | undefined;

    // Fetch details for display
    if (ownerId) {
      const collectionName = type === "kitchen" ? "kitchens" : "users";
      try {
        const snap = await getDoc(doc(db, collectionName, ownerId));
        if (snap.exists()) {
            const profile = snap.data();
            ownerName = profile.name || profile.kitchen_name;
            ownerPhone = profile.phone;
        }
      } catch (e) {
          console.warn(`Could not fetch details for ${ownerId}`);
      }
    }

    wallets.push({
      id: d.id,
      ...data,
      ownerName,
      ownerPhone,
    } as WalletData);
  }

  return wallets;
};

// --- 2. SETTLEMENT (server-side) ---

/** Settlement now runs server-side (processKitchenSettlements schedule / adminWalletOp batchSettle). */
export const processOrderSettlement = async (_order: KitchenOrder) => {
  return batchSettlePendingOrders();
};

/** Admin Action: Run Settlement Payout (dueBalance -> paid out). */
export const runSettlementPayout = async (kitchenId: string, amount: number, notes: string) => {
  await adminWalletOp({ op: "payout", walletId: kitchenId, amount, notes });
  return true;
};

// --- 3. STANDARD WALLET UTILS ---

/** Admin: credit/debit a wallet (server mirrors users.walletBalance + writes ledger). */
export const adjustWalletBalance = async (
  walletId: string,
  amount: number,
  type: "credit" | "debit",
  reason: string
) => {
  await adminWalletOp({ op: "adjust", walletId, amount, direction: type, reason });
};

/** Admin "Sync & Settle": settle all delivered, unsettled kitchen orders now. */
export const batchSettlePendingOrders = async (): Promise<{ settled: number; message: string }> => {
  return adminWalletOp({ op: "batchSettle" });
};

// Get History
export const getWalletTransactions = async (walletId: string) => {
  try {
    // Check main transaction collection first (New System)
    const q = query(
        collection(db, "wallet_transactions"), 
        where("walletId", "==", walletId),
        orderBy("createdAt", "desc"),
        limit(50)
    );
    const snap = await getDocs(q);
    
    // Fallback to legacy subcollection if empty
    if (snap.empty) {
        const legacyQ = query(
            collection(db, "wallets", walletId, "transactions"),
            orderBy("date", "desc"),
            limit(20)
        );
        const legacySnap = await getDocs(legacyQ);
        return legacySnap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error("History fetch error", e);
    return [];
  }
};

/**
 * Customer-reported top-up / dues payment (UPI ref or cash handover).
 * Creates a pending wallet_requests doc; an admin verifies and credits via adminWalletOp.
 * (Wallets are never credited directly from the client.)
 */
export const reportPayment = async (
  walletId: string,
  amount: number,
  method: "upi" | "cash",
  referenceId: string
): Promise<void> => {
  if (!walletId) throw new Error("walletId required");
  if (!amount || amount <= 0) throw new Error("amount must be positive");
  await addDoc(collection(db, "wallet_requests"), {
    userId: walletId,
    amount: Math.round(amount * 100) / 100,
    method,
    transactionId: referenceId || "",
    status: "pending",
    createdAt: serverTimestamp(),
  });
};
